import React, { useState, useCallback } from 'react';
import * as XLSX from 'xlsx';
import { AiOutlineCloudUpload, AiOutlineCheck, AiOutlineCopy, AiOutlineDatabase, AiOutlineInfoCircle } from 'react-icons/ai';

// GLOBAL HELPERS
function formatDate(raw) {
    if (!raw) return "";
    if (typeof raw === "number") {
        const d = new Date(Math.round((raw - 25569) * 86400 * 1000));
        const dd = String(d.getUTCDate()).padStart(2, "0");
        const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
        return `${dd}-${mm}-${d.getUTCFullYear()}`;
    }
    let s = String(raw).trim();
    s = s.replace(/\//g, "-");
    if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
        const p = s.split(/[-T ]/);
        return `${p[2].substring(0, 2).padStart(2, "0")}-${p[1].padStart(2, "0")}-${p[0]}`;
    }
    const parts = s.split("-");
    if (parts.length === 3 && parts[2].length === 2) {
        const yr = parseInt(parts[2]);
        parts[2] = (yr > 50 ? "19" : "20") + parts[2];
        return parts.join("-");
    }
    return s;
}

function findSheet(wb, names) {
    for (const name of names) {
        const found = wb.SheetNames.find(
            s => s.trim().toLowerCase() === name.trim().toLowerCase()
        );
        if (found) return wb.Sheets[found];
    }
    return null;
}

function safeNum(v) {
    const n = parseFloat(v);
    return isNaN(n) ? 0 : n;
}

function absNum(v) {
    return Math.abs(safeNum(v));
}

function safeStr(v) {
    return v === null || v === undefined ? "" : String(v).trim();
}

const GSTR2BImportModule = ({ onImport }) => {
    const [loading, setLoading] = useState(false);
    const [dragging, setDragging] = useState(false);
    const [metadata, setMetadata] = useState(null);
    const [rows, setRows] = useState([]);
    const [stats, setStats] = useState(null);
    const [toast, setToast] = useState(null);
    const [copied, setCopied] = useState(false);

    const showToast = (msg, type = 'success') => {
        setToast({ msg, type });
        setTimeout(() => setToast(null), 3000);
    };

    const processFile = async (file) => {
        setLoading(true);
        try {
            const data = await file.arrayBuffer();
            const wb = XLSX.read(data);

            // Read Metadata (GSTIN)
            const readmeWs = findSheet(wb, ["Read me", "README"]) || wb.Sheets[wb.SheetNames[0]];
            const readmeRows = XLSX.utils.sheet_to_json(readmeWs, { header: 1, defval: "" });
            const selfGSTIN = safeStr(readmeRows?.[5]?.[2]) || safeStr(readmeRows?.[4]?.[2]) || "";
            const legalName = safeStr(readmeRows?.[6]?.[2]) || "";
            const taxPeriod = safeStr(readmeRows?.[4]?.[2]) || "";
            const fy = safeStr(readmeRows?.[3]?.[2]) || "";

            setMetadata({ gstin: selfGSTIN, legalName, taxPeriod, fy });

            let allRows = [];
            let counts = { b2b: 0, cdnr: 0, impg: 0, sez: 0, rcm: 0 };
            let taxes = { igst: 0, cgst: 0, sgst: 0 };

            // 1. B2B
            const b2bWs = findSheet(wb, ["B2B"]);
            if (b2bWs) {
                const b2bData = XLSX.utils.sheet_to_json(b2bWs, { header: 1, defval: "" });
                b2bData.slice(6).forEach(row => {
                    if (!safeStr(row[0]) && !safeStr(row[2])) return;
                    counts.b2b++;
                    const reverseCharge = safeStr(row[7]).toLowerCase() === "yes";
                    if (reverseCharge) counts.rcm++;
                    const istype = reverseCharge
                        ? "Inward supplies liable to reverse charge"
                        : "Inward Supply from Registered Person";

                    const igst = absNum(row[10]);
                    const cgst = absNum(row[11]);
                    const sgst = absNum(row[12]);
                    taxes.igst += igst; taxes.cgst += cgst; taxes.sgst += sgst;

                    allRows.push({
                        source: 'B2B',
                        rcm: reverseCharge,
                        data: [
                            istype,
                            safeStr(row[0]),
                            "Invoice/Bill of Entry",
                            safeStr(row[2]),
                            formatDate(row[4]),
                            "",
                            absNum(row[9]),
                            igst,
                            cgst,
                            sgst
                        ]
                    });
                });
            }

            // 2. CDNR
            const cdnrWs = findSheet(wb, ["B2B-CDNR", "CDNR"]);
            if (cdnrWs) {
                const cdnrData = XLSX.utils.sheet_to_json(cdnrWs, { header: 1, defval: "" });
                cdnrData.slice(6).forEach(row => {
                    if (!safeStr(row[0]) && !safeStr(row[3])) return;
                    counts.cdnr++;
                    const igst = absNum(row[11]);
                    const cgst = absNum(row[12]);
                    const sgst = absNum(row[13]);
                    taxes.igst += igst; taxes.cgst += cgst; taxes.sgst += sgst;

                    allRows.push({
                        source: 'CDNR',
                        data: [
                            "Inward Supply from Registered Person",
                            safeStr(row[0]),
                            "Credit Note",
                            safeStr(row[3]),
                            formatDate(row[5]),
                            "",
                            absNum(row[10]),
                            igst,
                            cgst,
                            sgst
                        ]
                    });
                });
            }

            // 3. IMPG
            const impgWs = findSheet(wb, ["IMPG"]);
            if (impgWs) {
                const impgData = XLSX.utils.sheet_to_json(impgWs, { header: 1, defval: "" });
                impgData.slice(6).forEach(row => {
                    if (!safeStr(row[2])) return;
                    counts.impg++;
                    const igst = safeNum(row[5]);
                    taxes.igst += igst;

                    allRows.push({
                        source: 'IMPG',
                        data: [
                            "Import of Goods/Supplies from SEZ to DTA",
                            selfGSTIN,
                            "Invoice/Bill of Entry",
                            safeStr(row[2]),
                            formatDate(row[3]),
                            safeStr(row[1]),
                            safeNum(row[4]),
                            igst,
                            0,
                            0
                        ]
                    });
                });
            }

            // 4. IMPG SEZ
            const sezWs = findSheet(wb, ["IMPG SEZ", "IMPG_SEZ", "IMPG-SEZ", "IMPGSEZ", "IMPG (SEZ)"]);
            if (sezWs) {
                const sezData = XLSX.utils.sheet_to_json(sezWs, { header: 1, defval: "" });
                sezData.slice(6).forEach(row => {
                    if (!safeStr(row[4])) return;
                    counts.sez++;
                    const igst = safeNum(row[7]);
                    taxes.igst += igst;

                    allRows.push({
                        source: 'SEZ',
                        data: [
                            "Import of Goods/Supplies from SEZ to DTA",
                            selfGSTIN,
                            "Invoice/Bill of Entry",
                            safeStr(row[4]),
                            formatDate(row[5]),
                            safeStr(row[3]),
                            safeNum(row[6]),
                            igst,
                            0,
                            0
                        ]
                    });
                });
            }

            setRows(allRows);
            setStats({ ...counts, ...taxes });
            showToast(`Parsed ${allRows.length} rows from GSTR-2B`);
        } catch (err) {
            console.error(err);
            showToast("Failed to parse Excel file", "error");
        }
        setLoading(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setDragging(false);
        const file = e.dataTransfer.files[0];
        if (file) processFile(file);
    };

    const handleFileSelect = (e) => {
        const file = e.target.files[0];
        if (file) processFile(file);
    };

    const handleImport = () => {
        if (rows.length === 0) return;
        // Transform internal row format to the format nonRoutine/Statement01AModule expects
        // Which is just the [0..9] array of inward fields
        const finalData = rows.map(r => r.data);
        onImport(finalData);
        showToast(`✓ GSTR-2B imported! ${rows.length} rows added to Statement 01A`);
    };

    const copyTSV = () => {
        const head = "Type\tGSTIN\tDocType\tNo\tDate\tPort\tTaxable\tIGST\tCGST\tSGST";
        const body = rows.map(r => r.data.join("\t")).join("\n");
        navigator.clipboard.writeText(head + "\n" + body).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    const formatINR = (val) => new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 }).format(val);

    return (
        <div className="gstr2b-import-module">
            <style>{`
                .gstr2b-import-module { background: transparent; color: #f8fafc; padding: 0; border-radius: 12px; font-family: 'DM Mono', monospace; }
                .syne { font-family: 'Syne', sans-serif; }
                
                .upload-zone { border: 2px dashed #1e293b; border-radius: 16px; padding: 40px; text-align: center; cursor: pointer; transition: all 0.3s; background: #111827; position: relative; margin-bottom: 24px; }
                .upload-zone.dragging { border-color: #f59e0b; background: rgba(245, 158, 11, 0.05); transform: scale(1.01); }
                .upload-zone:hover { border-color: #f59e0b; }
                .upload-zone input { position: absolute; inset: 0; opacity: 0; cursor: pointer; }
                
                .metadata-card { background: #111827; border: 1px solid #1e293b; border-radius: 12px; padding: 20px; margin-bottom: 24px; display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 20px; }
                .meta-item label { display: block; color: #64748b; font-size: 11px; margin-bottom: 4px; text-transform: uppercase; letter-spacing: 0.5px; }
                .meta-item span { font-size: 14px; color: #f59e0b; font-weight: bold; }

                .stats-bar { background: #111827; border: 1px solid #1e293b; border-radius: 12px; padding: 16px 24px; margin-bottom: 24px; display: flex; flex-wrap: wrap; gap: 30px; align-items: center; }
                .stat-chunk { display: flex; flex-direction: column; }
                .stat-chunk label { color: #64748b; font-size: 10px; text-transform: uppercase; }
                .stat-chunk span { font-size: 16px; font-weight: 800; color: #f59e0b; }
                .stat-divider { width: 1px; height: 30px; background: #1e293b; }

                .table-container { background: #111827; border: 1px solid #1e293b; border-radius: 12px; overflow: hidden; margin-bottom: 24px; }
                .table-scroll { max-height: 450px; overflow: auto; }
                table { width: 100%; border-collapse: collapse; font-size: 12px; }
                th { background: #0f172a; color: #64748b; text-align: left; padding: 12px 16px; font-weight: 500; text-transform: uppercase; letter-spacing: 0.5px; position: sticky; top: 0; z-index: 10; border-bottom: 1px solid #1e293b; }
                td { padding: 10px 16px; border-bottom: 1px solid #0f172a; color: #cbd5e1; }
                
                tr.row-B2B { background: rgba(245, 158, 11, 0.03); }
                tr.row-B2B-RCM { background: rgba(251, 113, 133, 0.05); }
                tr.row-CDNR { background: rgba(251, 113, 133, 0.08); }
                tr.row-IMPG { background: rgba(45, 212, 191, 0.05); }
                tr.row-SEZ { background: rgba(167, 139, 250, 0.05); }

                .badge { padding: 2px 6px; border-radius: 4px; font-size: 9px; font-weight: 800; text-transform: uppercase; margin-right: 6px; }
                .badge-b2b { background: rgba(245, 158, 11, 0.2); color: #f59e0b; }
                .badge-rcm { background: rgba(251, 113, 133, 0.2); color: #fb7185; }
                .badge-cdnr { background: rgba(251, 113, 133, 0.3); color: #fb7185; }
                .badge-impg { background: rgba(45, 212, 191, 0.2); color: #2dd4bf; }
                .badge-sez { background: rgba(167, 139, 250, 0.2); color: #a78bfa; }

                .actions { display: flex; gap: 16px; align-items: center; }
                .btn-primary { background: #f59e0b; color: #0b0f1a; border: none; padding: 12px 24px; border-radius: 8px; font-weight: 800; cursor: pointer; display: flex; align-items: center; gap: 8px; transition: all 0.3s; flex: 1; justify-content: center; font-size: 14px; }
                .btn-primary:hover:not(:disabled) { box-shadow: 0 0 20px rgba(245, 158, 11, 0.3); transform: translateY(-1px); }
                .btn-primary:disabled { opacity: 0.3; cursor: not-allowed; }
                
                .btn-copy { background: transparent; border: 1px solid #1e293b; color: #64748b; padding: 12px 20px; border-radius: 8px; cursor: pointer; display: flex; align-items: center; gap: 8px; transition: all 0.3s; font-size: 14px; }
                .btn-copy:hover { border-color: #64748b; color: #f8fafc; }

                .toast { position: fixed; bottom: 30px; right: 30px; padding: 16px 24px; border-radius: 8px; display: flex; align-items: center; gap: 12px; z-index: 1000; animation: slideIn 0.3s ease-out; box-shadow: 0 10px 30px rgba(0,0,0,0.5); font-weight: 600; }
                .toast-success { background: #064e3b; color: #34d399; border: 1px solid #059669; }
                .toast-error { background: #450a0a; color: #f87171; border: 1px solid #991b1b; }
                @keyframes slideIn { from { transform: translateX(100%); opacity: 0; } to { transform: translateX(0); opacity: 1; } }

                .empty-val { color: #334155; }
            `}</style>

            <div
                className={`upload-zone ${dragging ? 'dragging' : ''}`}
                onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                onDragLeave={() => setDragging(false)}
                onDrop={handleDrop}
                style={{ padding: '24px' }}
            >
                <input type="file" accept=".xlsx,.xls" onChange={handleFileSelect} />
                <AiOutlineCloudUpload size={40} color={dragging ? "#f59e0b" : "#1e293b"} style={{ marginBottom: '8px' }} />
                <div className="syne" style={{ fontSize: '16px', fontWeight: 700, marginBottom: '2px' }}>
                    {loading ? "Reading Ledger..." : "Upload GSTR-2B Excel"}
                </div>
                <div style={{ color: '#64748b', fontSize: '13px' }}>
                    Automatically parses B2B, CDNR, IMPG, and IMPG SEZ
                </div>
            </div>

            {metadata && (
                <div className="metadata-card">
                    <div className="meta-item">
                        <label>GSTIN</label>
                        <span>{metadata.gstin || "N/A"}</span>
                    </div>
                    <div className="meta-item">
                        <label>Legal Name</label>
                        <span>{metadata.legalName || "N/A"}</span>
                    </div>
                    <div className="meta-item">
                        <label>Tax Period</label>
                        <span>{metadata.taxPeriod}</span>
                    </div>
                    <div className="meta-item">
                        <label>Financial Year</label>
                        <span>{metadata.fy}</span>
                    </div>
                </div>
            )}

            {stats && (
                <div className="stats-bar">
                    <div className="stat-chunk">
                        <label>B2B Rows</label>
                        <span>{stats.b2b}</span>
                    </div>
                    <div className="stat-chunk">
                        <label>CDNR / IMPG / SEZ</label>
                        <span>{stats.cdnr + stats.impg + stats.sez}</span>
                    </div>
                    <div className="stat-chunk">
                        <label>Reverse Charge</label>
                        <span style={{ color: stats.rcm > 0 ? '#fb7185' : '#f59e0b' }}>{stats.rcm}</span>
                    </div>
                    <div className="stat-divider" />
                    <div className="stat-chunk">
                        <label>Total IGST</label>
                        <span style={{ color: '#2dd4bf' }}>₹{formatINR(stats.igst)}</span>
                    </div>
                    <div className="stat-chunk" style={{ opacity: 0.6 }}>
                        <label>CGST / SGST</label>
                        <span>₹{formatINR(stats.cgst)}</span>
                    </div>
                </div>
            )}

            {rows.length > 0 && (
                <>
                    <div className="table-container">
                        <div className="table-scroll">
                            <table>
                                <thead>
                                    <tr>
                                        <th>Source</th>
                                        <th>Inward Type</th>
                                        <th>GSTIN</th>
                                        <th>Doc Type</th>
                                        <th>Doc No</th>
                                        <th>Date</th>
                                        <th>Port</th>
                                        <th>Taxable</th>
                                        <th>IGST</th>
                                        <th>CGST/SGST</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((r, i) => {
                                        const [istype, gstin, dtype, no, date, port, val, igst, cgst, sgst] = r.data;
                                        const rowClass = `row-${r.source}${r.rcm ? '-RCM' : ''}`;
                                        const badgeClass = `badge badge-${r.source.toLowerCase()}${r.rcm ? '-rcm' : ''}`;

                                        return (
                                            <tr key={i} className={rowClass}>
                                                <td><span className={badgeClass}>{r.rcm ? 'RCM' : r.source}</span></td>
                                                <td style={{ fontSize: '10px', maxWidth: '150px' }}>{istype}</td>
                                                <td>{gstin}</td>
                                                <td>{dtype === 'Credit Note' ? <span style={{ color: '#fb7185' }}>CN</span> : dtype}</td>
                                                <td style={{ fontWeight: 700, color: '#f8fafc' }}>{no}</td>
                                                <td>{date}</td>
                                                <td>{port || <span className="empty-val">—</span>}</td>
                                                <td style={{ fontWeight: stats && stats.rcm > 0 && r.rcm ? '800' : '400' }}>{formatINR(val)}</td>
                                                <td style={{ color: '#2dd4bf' }}>{igst > 0 ? formatINR(igst) : <span className="empty-val">—</span>}</td>
                                                <td>{cgst > 0 ? formatINR(cgst) : <span className="empty-val">—</span>}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <div className="actions">
                        <button className="btn-primary" onClick={handleImport}>
                            <AiOutlineDatabase size={20} /> Import to Statement 01A — Inward Side
                        </button>
                        <button className="btn-copy" onClick={copyTSV}>
                            {copied ? <AiOutlineCheck color="#34d399" /> : <AiOutlineCopy size={18} />}
                            {copied ? "Copied ✓" : "Copy as TSV"}
                        </button>
                    </div>
                </>
            )}

            {!rows.length && !loading && (
                <div style={{ textAlign: 'center', padding: '60px', color: '#334155' }}>
                    <AiOutlineInfoCircle size={40} style={{ marginBottom: '12px', opacity: 0.3 }} />
                    <div>No data loaded. Prepare by uploading GSTR-2B Excel.</div>
                </div>
            )}

            {toast && (
                <div className={`toast toast-${toast.type}`}>
                    {toast.type === 'success' ? <AiOutlineCheck size={20} /> : "⚠"}
                    {toast.msg}
                </div>
            )}
        </div>
    );
};

export default GSTR2BImportModule;
